import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UnitService } from '@services/unit.service';
import { sharedResolution } from '../../../src/shared_resolution';
import { SpaContext } from '../Classes/spacontext';
import { TranslateService } from '../Common/Services/translate.service';
import { DropInQueueService } from '../Services/Queue/dropin-queue.service';

@Component({
	standalone: false,
	selector: 'drop-in-section-from-menu',
	// The only reason for the "injectionPatch" is due to miss matching source of
	// truth between the injected variant and the old variant. SpaContext could
	// lag behind...
	template: `
		<div class="wwrapper">
			<ng-container *ngIf="checkShouldRenderStatus()">
				<ng-container
					*ngIf="dropInQueueService.lastFetchedActiveTicket?.UnitId === spaContext.unitConfig.Unit.UnitId; else injectionPatch"
				>
					<a class="item" [routerLink]="getDropInLink()">
						<ng-container *ngTemplateOutlet="queuePlaceNotice"></ng-container>
					</a>
				</ng-container>

				<ng-template #injectionPatch>
					<a class="item" [href]="getDropInLink()">
						<ng-container *ngTemplateOutlet="queuePlaceNotice"></ng-container>
					</a>
				</ng-template>
			</ng-container>

			<ng-template #queuePlaceNotice>
				<div class="svgWrap">
					<span>{{ dropInQueueService.queue?.data?.placeInQueue }}</span>
					<visiba-svg a="PartialQueue"></visiba-svg>
				</div>
				<div class="content">
					<div class="mainContent">
						{{
							'LicenceHolder.Component.Menu.Notification.DropInQueue.Title'
								| translate: { roomName: dropInQueueService.room.roomInfo.Name }
						}}
					</div>
					<div class="subContent">
						{{
							'LicenceHolder.Component.Menu.Notification.DropInQueue.Description'
								| translate: { placeInQueue: dropInQueueService.queue?.data?.placeInQueue }
						}}
					</div>
				</div>
			</ng-template>
		</div>
	`,
	encapsulation: ViewEncapsulation.None,
	styleUrls: ['../Css/Components/drop-in-section-from-menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropInSectionFromMenuComponent implements OnInit, OnDestroy {
	private readonly subscribers = [];
	private readonly unitService = sharedResolution.resolve(UnitService);
	private styleElement: HTMLStyleElement | null = null;

	constructor(
		public readonly spaContext: SpaContext,
		public readonly translateService: TranslateService,
		public readonly dropInQueueService: DropInQueueService,
		private readonly changeDetectorRef: ChangeDetectorRef,
	) {
		// Empty
	}

	ngOnInit() {
		this.subscribers.push(
			this.dropInQueueService.event.subscribe((event) => {
				const shouldRender = this.checkShouldRenderStatus();
				if (shouldRender) {
					const sheet = this.createSheet();
					if (sheet.cssRules.length > 0) {
						sheet.deleteRule(0);
					}
					sheet.insertRule(`
						.__TEMP__dropInIsOpen {
							top: 180px !important;
						}
					`);
				} else {
					this.removeSheet();
				}

				this.changeDetectorRef.detectChanges();
			}),
		);
	}

	ngOnDestroy() {
		this.subscribers.forEach((subscriber) => subscriber.unsubscribe());

		this.removeSheet();
	}

	public getDropInLink() {
		// The relation between lastFetchedActiveTicket and "currentTicket" makes
		// no sense but we seemed to have been using them interchangeably in the
		// past. So we'll do it now as well!
		const ticketUnitId = this.dropInQueueService.lastFetchedActiveTicket.UnitId;
		const unit = this.unitService.getUnitNodeInTree(ticketUnitId);

		return `/${unit.route}/dropin/${this.dropInQueueService.currentUrlAlias}/${this.dropInQueueService.currentTicket}`;
	}

	public checkShouldRenderStatus(): boolean {
		return this.dropInQueueService.isAlive && this.dropInQueueService.queue?.data?.placeInQueue !== null;
	}

	private removeSheet(): void {
		if (this.styleElement != null) {
			this.styleElement.remove();
			this.styleElement = null;
		}
	}

	private createSheet(): CSSStyleSheet {
		if (this.styleElement) return this.styleElement.sheet;

		const styleElement = document.createElement('style');
		styleElement.appendChild(document.createTextNode(''));
		styleElement.type = 'text/css';
		document.head.appendChild(styleElement);

		this.styleElement = styleElement;

		return styleElement.sheet;
	}
}
